import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import {
  Users,
  Briefcase,
  CheckCircle2,
  Package,
  Loader2,
  Activity,
  AlertTriangle,
  ClipboardCheck,
  HardHat,
  Wrench,
  ArrowRight,
  Calendar as CalendarIcon,
  X,
  FileText,
  FileSpreadsheet,
  Clock,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import FollowupDashboardPanel from "../components/FollowupDashboardPanel";
import VeicoliScadenzePanel from "../components/VeicoliScadenzePanel";
import DaPianificarePanel from "../components/DaPianificarePanel";
import AttesaFornitorePanel from "../components/AttesaFornitorePanel";
import InterventiDaPianificarePanel from "../components/InterventiDaPianificarePanel";
import { useAuth } from "../context/AuthContext";
import { canDoAction, canSeeMenu } from "../lib/menu";

function Metric({ icon: Icon, label, value, tone = "primary", testid }) {
  const color = tone === "warn" ? "#D97706" : tone === "accent" ? "var(--accent)" : "var(--primary)";
  return (
    <div className="card-shell p-5 flex gap-4 items-center" data-testid={testid}>
      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, color }}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div>
        <div className="metric-number">{value}</div>
      </div>
    </div>
  );
}

// SVG sparkline (barre verticali), 6 barre per gli ultimi 6 mesi.
// Etichette x = mese breve italiano. Tooltip nativo via <title>.
const MESI_BREVI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
function shortMonth(iso) {
  // iso = "YYYY-MM"
  const m = parseInt(iso.split("-")[1], 10) - 1;
  return MESI_BREVI[m] || iso;
}

function Sparkline({ data, color = "var(--primary)", testid }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  const W = 160;
  const H = 36;
  const gap = 4;
  const barW = (W - gap * (data.length - 1)) / data.length;
  return (
    <div className="flex flex-col gap-1" data-testid={testid}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Trend ultimi 6 mesi">
        {data.map((d, i) => {
          const h = d.count === 0 ? 2 : Math.max(2, (d.count / max) * (H - 4));
          const x = i * (barW + gap);
          const y = H - h;
          const isLast = i === data.length - 1;
          return (
            <rect
              key={d.month}
              x={x}
              y={y}
              width={barW}
              height={h}
              rx={2}
              fill={isLast ? color : `${color}55`}
            >
              <title>{`${shortMonth(d.month)}: ${d.count}`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="flex justify-between text-[9px] font-medium tracking-wide" style={{ color: "var(--text-muted)" }}>
        {data.map((d) => (
          <span key={d.month} className="w-6 text-center">{shortMonth(d.month)}</span>
        ))}
      </div>
    </div>
  );
}

function ModuleActivityCard({ icon: Icon, label, periodo, totale, trend, slug, periodoLabel, dateFrom, dateTo, testid }) {
  const navigate = useNavigate();
  const goToList = () => {
    const params = new URLSearchParams();
    params.set("progress_module", slug);
    params.set("progress_status", "done");
    navigate(`/clienti?${params.toString()}`);
  };
  return (
    <button
      onClick={goToList}
      className="card-shell p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex gap-3 items-center min-w-0">
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "var(--primary)15", color: "var(--primary)" }}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div>
            <div className="font-bold text-base truncate" style={{ fontFamily: "Manrope" }}>Completati</div>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 shrink-0 mt-1" style={{ color: "var(--text-muted)" }} />
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="rounded-md px-3 py-2" style={{ background: "var(--surface-muted)" }}>
          <div className="text-[10px] uppercase font-semibold tracking-wider truncate" style={{ color: "var(--text-muted)" }} title={periodoLabel}>
            {periodoLabel}
          </div>
          <div className="text-2xl font-extrabold leading-tight" style={{ fontFamily: "Manrope", color: "var(--primary)" }} data-testid={`${testid}-periodo`}>
            {periodo ?? 0}
          </div>
        </div>
        <div className="rounded-md px-3 py-2" style={{ background: "var(--surface-muted)" }}>
          <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>Totale</div>
          <div className="text-2xl font-extrabold leading-tight" style={{ fontFamily: "Manrope" }} data-testid={`${testid}-totale`}>
            {totale ?? 0}
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t flex items-end justify-between gap-3" style={{ borderColor: "var(--border)" }}>
        <div>
          <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>Trend 6 mesi</div>
          <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>completati / mese</div>
        </div>
        <Sparkline data={trend || []} testid={`${testid}-trend`} />
      </div>
    </button>
  );
}

// ---- Date range presets ----
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function firstOfMonthISO(d = new Date()) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
}
function lastOfMonthISO(d = new Date()) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
}
function daysAgoISO(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

const PRESETS = [
  { id: "this_month", label: "Questo mese", from: () => firstOfMonthISO(), to: () => todayISO() },
  { id: "last_month", label: "Mese scorso", from: () => {
      const d = new Date(); d.setUTCDate(0); return firstOfMonthISO(d);
    }, to: () => {
      const d = new Date(); d.setUTCDate(0); return lastOfMonthISO(d);
    } },
  { id: "last_30", label: "Ultimi 30 gg", from: () => daysAgoISO(30), to: () => todayISO() },
  { id: "last_90", label: "Ultimi 90 gg", from: () => daysAgoISO(90), to: () => todayISO() },
  { id: "ytd", label: "Da inizio anno", from: () => `${new Date().getUTCFullYear()}-01-01`, to: () => todayISO() },
];

function DateRangePicker({ dateFrom, dateTo, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const applyPreset = (p) => {
    onChange(p.from(), p.to());
    setOpen(false);
  };
  const activePreset = useMemo(() => {
    return PRESETS.find((p) => p.from() === dateFrom && p.to() === dateTo)?.label;
  }, [dateFrom, dateTo]);

  const labelChip = !dateFrom && !dateTo
    ? "Questo mese (default)"
    : activePreset || `${dateFrom || "—"} → ${dateTo || "—"}`;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border bg-white hover:bg-black/[0.02] transition"
        style={{ borderColor: "var(--border)" }}
        data-testid="date-range-trigger"
      >
        <CalendarIcon className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
        <span className="font-medium">{labelChip}</span>
        {(dateFrom || dateTo) && (
          <span
            onClick={(e) => { e.stopPropagation(); onClear(); setOpen(false); }}
            className="p-0.5 rounded hover:bg-black/5"
            data-testid="date-range-clear"
            role="button"
            aria-label="Pulisci intervallo"
          >
            <X className="w-3.5 h-3.5" />
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 z-40 card-shell p-4 w-[320px] shadow-xl"
            data-testid="date-range-popover"
          >
            <div className="text-xs uppercase font-semibold tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
              Preset
            </div>
            <div className="grid grid-cols-2 gap-1.5 mb-4">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => applyPreset(p)}
                  className="text-xs px-2.5 py-1.5 rounded-md border hover:bg-[var(--surface-muted)] text-left"
                  style={{ borderColor: "var(--border)" }}
                  data-testid={`date-preset-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="text-xs uppercase font-semibold tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
              Personalizzato
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[11px] font-semibold" style={{ color: "var(--text-muted)" }}>
                Da
                <input
                  type="date"
                  value={dateFrom || ""}
                  onChange={(e) => onChange(e.target.value, dateTo)}
                  className="mt-1 w-full px-2 py-1.5 text-sm rounded-md border outline-none"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="date-from"
                />
              </label>
              <label className="text-[11px] font-semibold" style={{ color: "var(--text-muted)" }}>
                A
                <input
                  type="date"
                  value={dateTo || ""}
                  onChange={(e) => onChange(dateFrom, e.target.value)}
                  className="mt-1 w-full px-2 py-1.5 text-sm rounded-md border outline-none"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="date-to"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { onClear(); setOpen(false); }} className="text-xs font-semibold hover:underline" style={{ color: "var(--accent)" }} data-testid="date-clear-all">
                Pulisci
              </button>
              <button onClick={() => setOpen(false)} className="btn-primary !py-1 !px-3 !text-xs" data-testid="date-apply">
                Applica
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(null); // 'pdf' | 'xlsx' | null

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data } = await api.get(`/dashboard/stats?${params.toString()}`);
      setStats(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  const periodoLabel = useMemo(() => {
    if (!dateFrom && !dateTo) return "Questo mese";
    if (dateFrom && dateTo) return `${dateFrom} → ${dateTo}`;
    if (dateFrom) return `Da ${dateFrom}`;
    return `Fino al ${dateTo}`;
  }, [dateFrom, dateTo]);

  const downloadBlob = async (path, filename, kind) => {
    setExporting(kind);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const res = await api.get(`${path}?${params.toString()}`, { responseType: "blob" });
      const ct = res.headers["content-type"] || (kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const url = window.URL.createObjectURL(new Blob([res.data], { type: ct }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Report ${kind.toUpperCase()} scaricato`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExporting(null);
    }
  };

  const exportPdf = () => {
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
    downloadBlob("/dashboard/export.pdf", `report_kpi_${ts}.pdf`, "pdf");
  };
  const exportXlsx = () => {
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
    downloadBlob("/dashboard/export.xlsx", `report_kpi_${ts}.xlsx`, "xlsx");
  };

  if (loading && !stats) return <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;

  const mod = stats?.moduli_attivita || {};

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Panoramica</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            Lo stato attuale delle tue commesse e dei clienti
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canDoAction(user, "export_data") && (
          <>
          <button
            onClick={exportPdf}
            disabled={exporting}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="export-kpi-pdf"
            title="Esporta i KPI del periodo selezionato in PDF"
          >
            {exporting === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF
          </button>
          <button
            onClick={exportXlsx}
            disabled={exporting}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="export-kpi-xlsx"
            title="Esporta i KPI del periodo selezionato in Excel"
          >
            {exporting === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Excel
          </button>
          </>
          )}
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={(f, t) => { setDateFrom(f); setDateTo(t); }}
            onClear={() => { setDateFrom(""); setDateTo(""); }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Metric icon={Users} label="Clienti" value={stats?.clienti_totali ?? 0} testid="kpi-clienti" />
        <Metric icon={Briefcase} label="Commesse attive" value={stats?.commesse_attive ?? 0} testid="kpi-commesse" />
        <Metric icon={Activity} label="In corso" value={stats?.clienti_in_corso ?? 0} tone="accent" testid="kpi-in-corso" />
        <Metric icon={Clock} label="Attesa fornitore" value={stats?.clienti_attesa_fornitore ?? 0} tone="warn" testid="kpi-attesa-fornitore" />
        <Metric icon={CheckCircle2} label="Completati" value={stats?.clienti_completati ?? 0} testid="kpi-completati" />
        <Metric icon={Package} label="Prodotti" value={stats?.prodotti_totali ?? 0} testid="kpi-prodotti" />
        <Metric icon={AlertTriangle} label="Sotto scorta" value={stats?.prodotti_sotto_scorta ?? 0} tone="accent" testid="kpi-sotto-scorta" />
      </div>

      <FollowupDashboardPanel />

      {["admin", "operatore"].includes(user?.role) && canSeeMenu(user, "installazioni_da_pianificare") && <DaPianificarePanel />}

      {["admin", "operatore"].includes(user?.role) && <AttesaFornitorePanel />}

      {["admin", "operatore", "tecnico"].includes(user?.role) && canSeeMenu(user, "interventi_da_pianificare") && <InterventiDaPianificarePanel />}

      <VeicoliScadenzePanel />

      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Moduli completati</h2>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              Sopralluoghi, installazioni e interventi con tutte le sezioni completate (stessi conteggi di Analytics). Clicca una card per vedere i clienti.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <ModuleActivityCard
            icon={ClipboardCheck}
            label="Sopralluogo"
            periodo={mod.sopralluogo?.periodo}
            totale={mod.sopralluogo?.totale}
            trend={mod.sopralluogo?.trend_6m}
            slug="sopralluogo"
            periodoLabel={periodoLabel}
            dateFrom={dateFrom}
            dateTo={dateTo}
            testid="kpi-sopralluogo"
          />
          <ModuleActivityCard
            icon={HardHat}
            label="Installazione"
            periodo={mod.installazione?.periodo}
            totale={mod.installazione?.totale}
            trend={mod.installazione?.trend_6m}
            slug="installazione"
            periodoLabel={periodoLabel}
            dateFrom={dateFrom}
            dateTo={dateTo}
            testid="kpi-installazione"
          />
          <ModuleActivityCard
            icon={Wrench}
            label="Interventi"
            periodo={mod.intervento?.periodo}
            totale={mod.intervento?.totale}
            trend={mod.intervento?.trend_6m}
            slug="intervento"
            periodoLabel={periodoLabel}
            dateFrom={dateFrom}
            dateTo={dateTo}
            testid="kpi-intervento"
          />
        </div>
      </div>

      <div className="card-shell p-6" data-testid="quick-actions">
        <h2 className="font-bold text-lg mb-4" style={{ fontFamily: "Manrope" }}>Azioni rapide</h2>
        <div className="grid sm:grid-cols-3 gap-2">
          <Link to="/clienti" className="btn-primary justify-center" data-testid="qa-clienti">+ Nuovo cliente</Link>
          <Link to="/commesse" className="btn-secondary justify-center" data-testid="qa-commesse">Gestisci commesse</Link>
          <Link to="/prodotti" className="btn-secondary justify-center" data-testid="qa-prodotti">Magazzino prodotti</Link>
        </div>
        <div
          className="mt-6 rounded-lg p-4 text-xs leading-relaxed"
          style={{ background: "#E8F3EB", color: "#1E4620" }}
        >
          <div className="font-bold mb-1">Suggerimento</div>
          Ogni commessa può abilitare moduli diversi: Sopralluogo, Installazione e Interventi. Configura i moduli sulla commessa prima di creare i clienti.
        </div>
      </div>
    </div>
  );
}
